import Tool from './tool';
import logging from '../logging';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Camera, Vector3 } from 'three';
import Signal from '../control-flow/signal';

const log = logging('tools');

/**
 * This orbit tool wraps the three.js OrbitControls.
 */
export class OrbitTool extends Tool {

    camera: Camera;
    canvas: HTMLCanvasElement;
    target: Vector3 = new Vector3();

    controls: OrbitControls;

    onChange: Signal = new Signal('onChange', 'tools');

    constructor(camera: Camera, canvas: HTMLCanvasElement) {
        super();

        this.camera = camera;
        this.canvas = canvas;
        this.controls = new OrbitControls(camera, canvas);

        this.applyPreset("constrained");
        this.controls.target = this.target;
        this.controls.enabled = true;

        this.controls.update();
    }

    enable(): void {
        log.debug('OrbitTool.enable()');
        this.controls.enabled = true;
        this.controls.object = this.camera;
        // remove a listener first to avoid double registration
        this.canvas.removeEventListener(
            'pointerdown', this.begin);
        this.canvas.addEventListener('pointerdown', this.begin);

        this.canvas.removeEventListener('wheel', this.onChange.trigger);
        this.canvas.addEventListener('wheel', this.onChange.trigger);

        this.update();
    }

    disable(): void {
        log.debug('OrbitTool.disable()');
        this.controls.enabled = false;
        this.canvas.removeEventListener(
            'pointerdown', this.begin);
        this.canvas.removeEventListener('wheel', this.onChange.trigger);
    }

    /**
     * This method expects to be called by a pointerdown event
     */
    begin(): void {
        log.debug('OrbitTool.begin()');
        // We update this on every pointer-down in case it changed since
        // last time, i.e. switching from ortho to perspective
        this.controls.object = this.camera;
        this.controls.addEventListener('change', this.onChange.trigger);

        window.addEventListener('pointerup', this.end, {once: true});
    }

    cancel(): void {
        log.debug('OrbitTool.cancel()');
        this.controls.removeEventListener('change', this.onChange.trigger);
    }

    end(): void {
        log.debug('OrbitTool.end()');
        this.cancel();
    }

    /**
     * This should be called before render to update the controls
     */
    update = () => {
        //log.debug('OrbitTool.update()');
        if(this.controls.enabled) this.controls.update();
    }

    applyPreset(preset: "free" | "constrained") {
        switch(preset) {
            case "free":
                this.controls.enablePan = true;
                this.controls.enableDamping = false;
                this.controls.dampingFactor = 0.05;
                this.controls.screenSpacePanning = true;
                this.controls.minAzimuthAngle = -Infinity;
                this.controls.maxAzimuthAngle = Infinity;
                this.controls.minDistance = 0;
                this.controls.maxDistance = Infinity;
                break;
            case "constrained":
                this.controls.enablePan = false;
                this.controls.enableDamping = true;
                this.controls.dampingFactor = 0.25;
                this.controls.screenSpacePanning = false;
                this.controls.minAzimuthAngle = -Math.PI/2;
                this.controls.maxAzimuthAngle = Math.PI/2;
                this.controls.minDistance = 24;
                this.controls.maxDistance = 90;
                break;
        }
    }
}
export default OrbitTool;
